'use client';

import cn from 'classnames';
import { type FocusEvent, type ReactNode } from 'react';
import { Form } from '@/ui-components/atoms/Inputs/Form/Form';
import { Input } from '@/ui-components/atoms/Inputs/Input/Input';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Link } from '@/ui-components/atoms/Link/Link';
import { VALIDITY } from '@/constants';
import NotificationMessage from '@/ui-components/organisms/Snapshot/NotificationMessage/NotificationMessage';
import {
    SnapshotAlertBanner,
    type SnapshotAlertBannerProps,
} from '@/ui-components/organisms/AccountSnapshot/SnapshotAlertBanner';
import styles from './signIn.module.scss';

const { innerFormWrapper, inputFieldsWrapper, linkStylesLeft, verificationCodeHeaderWrapper } = styles;

export interface SignInVerificationCodeBodyProps {
    destinationLabel?: string;
    invalidMessage?: ReactNode;
    isSubmitting?: boolean;
    notificationMessageKey?: number;
    notificationPhase?: 'enter' | 'exit';
    notificationBanner?: SnapshotAlertBannerProps;
    onRequestNewCode?: () => void | Promise<void>;
    onSubmit: (event: React.FormEvent<HTMLFormElement>, data: FormData) => void | Promise<void>;
    onVerificationCodeBlur?: (event: FocusEvent<HTMLInputElement>) => void;
    notificationMessage?: string;
    verificationCode: string;
    onVerificationCodeChange: (value: string) => void;
    showErrorSummary?: boolean;
}

const noop = () => undefined;

export function SignInVerificationCodeBody({
    destinationLabel,
    invalidMessage,
    isSubmitting = false,
    notificationMessageKey,
    notificationPhase = 'enter',
    notificationBanner,
    onRequestNewCode,
    onSubmit,
    onVerificationCodeBlur,
    notificationMessage,
    onVerificationCodeChange,
    showErrorSummary = false,
    verificationCode,
}: SignInVerificationCodeBodyProps): React.JSX.Element {
    return (
        <>
            <div className="show-for-sr" role="status" aria-live="polite" aria-atomic="true">
                {notificationMessage || ''}
            </div>
            {notificationMessage && (
                <div
                    key={notificationMessageKey}
                    className={cn(styles.notificationMessageWrapper, {
                        [styles.notificationMessageEnter]: notificationPhase === 'enter',
                        [styles.notificationMessageExit]: notificationPhase === 'exit',
                    })}
                >
                    <NotificationMessage message={notificationMessage} success={true} />
                </div>
            )}
            <div className={verificationCodeHeaderWrapper}>
                <Text tag="h6" variant="h6">
                    Enter the verification code
                </Text>
                {destinationLabel && (
                    <Text tag="p" variant="text-medium">
                        {`We sent it to ${destinationLabel}`}
                    </Text>
                )}
            </div>
            {notificationBanner && <SnapshotAlertBanner {...notificationBanner} layout="inline" />}
            <div className={innerFormWrapper}>
                {showErrorSummary && (
                    <Text tag="p" variant="text-medium" className={styles.errorSummaryText}>
                        Please fix the error in the form below.
                    </Text>
                )}
                <Form
                    action=""
                    method="POST"
                    contentType="urlencoded"
                    onSubmit={(event, data) => {
                        void onSubmit(event as React.FormEvent<HTMLFormElement>, data);
                        return false;
                    }}
                    onFail={noop}
                    onSuccess={noop}
                    onBeforeSubmit={(data) => data}
                    onChange={noop}
                    novalidate={true}
                    dataCy="sign-in-verification-code-form"
                >
                    <div className={inputFieldsWrapper}>
                        <Input
                            required
                            type="text"
                            name="verification-code"
                            label="Verification code"
                            value={verificationCode}
                            onBlur={onVerificationCodeBlur}
                            onChange={(event) => onVerificationCodeChange(event.target.value)}
                            invalid={invalidMessage ? VALIDITY.invalid : VALIDITY.untouched}
                            invalidMessage={invalidMessage}
                        />
                        <Link
                            className={linkStylesLeft}
                            href=""
                            onClick={(event) => {
                                event.preventDefault();
                                void onRequestNewCode?.();
                            }}
                        >
                            Request new code
                        </Link>
                    </div>
                    <Button
                        type="submit"
                        ariaLabel="sign in"
                        variant="primary"
                        size="small"
                        fitting="fill-container"
                        dataCy="Sign-in-form-submit-button"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Continuing...' : 'Continue'}
                    </Button>
                </Form>
            </div>
        </>
    );
}
